/**
 * The harvest festival (D244, D245, D246): the prestige. Before it can be held, what it is
 * and how far the year has to go. Once it can, the Heritage on offer, the income bonus now
 * and after, what resets and what stays, and Hold behind a confirmation. Below, the perk
 * shop, bought with Heritage. After the tenth festival of a generation, passing the world on
 * (D257), and the traits bought with Lineage.
 */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  idleFestivalPreview,
  idleHeritageBonus,
  idleFestivalsThisGeneration,
  idlePassOnPreview,
  idleTraitCost,
  type IdleIntent,
} from '@hedgerow/sim';
import { Fanfare, flyCoins, playSound, popNumber, useReducedMotion } from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { UNSTRUCK } from '../ui/OffNote.tsx';
import { BuyButton, Figs } from './BuyButton.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { Icon } from '../ui/Icon.tsx';
import {
  BookSwitch,
  LockGlyph,
  NOTE,
  SectionHead,
  TAB_BODY,
  useArrival,
  useArrive,
  useBadges,
} from './tab-ui.tsx';
import { firstSegment, heritageMarks } from './badges.ts';
import { TreePanel } from './TreeView.tsx';
import { perkTreeProps as perkTree } from './tree-view.ts';
import { coins, num, useScientific } from './notation.ts';
import { idleContent, useIdle } from './store.ts';
import { cap, lower, t as term } from './terms.ts';
import {
  bonusText,
  festivalAdvice,
  heritageGrowthText,
  markIcon,
  markName,
  markGainLine,
  markStaysLine,
  yearOfGeneration,
  festivalView,
  passOnText,
  heritageTraitNext,
  refusalText,
  traitRows,
  type TraitRow,
} from './view.ts';

const CARD: JSX.CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: 10,
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
};
const H3: JSX.CSSProperties = { margin: 0, font: '600 var(--card-title)/1.2 var(--font-ui)' };
const SMALL: JSX.CSSProperties = {
  font: '400 var(--fs-caption)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
};
const LEAD: JSX.CSSProperties = { margin: 0, font: '600 var(--fs-label)/1.35 var(--font-ui)' };
const SUMMARY: JSX.CSSProperties = {
  cursor: 'pointer',
  font: '500 var(--fs-label)/1.3 var(--font-ui)',
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
};
const LIST: JSX.CSSProperties = { margin: 0, padding: '0 0 0 18px', display: 'grid', gap: 4 };
const P: JSX.CSSProperties = { margin: 0, font: '400 var(--fs-body)/1.4 var(--font-ui)' };

/** D371: details folded behind one plain label, closed until asked for. */
function HowItWorks({ testId, children }: { testId: string; children: ComponentChildren }) {
  return (
    <details data-testid={testId}>
      <summary style={SUMMARY}>How it works</summary>
      <div style={{ display: 'grid', gap: 8 }}>{children}</div>
    </details>
  );
}

/** Intents with no arguments; see `UPGRADE_BARN` in IdleApp for why they are cast. */
const HOLD_FESTIVAL = { type: 'holdFestival' } as IdleIntent;
const PASS_ON = { type: 'passOn' } as IdleIntent;

/** What the festival resets and what it keeps (D246: the barn stays). */
export const RESETS = `${cap(term('coins'))}, ${term('crops')}, the ${term('almanac')} and goals start again.`;
export const STAYS = `The ${term('barn')}, ${term('heritage')} and perks stay.`;

/**
 * One row of a shop, perks or traits: what it does, its level, what it adds up to (D295), and
 * Buy with its cost. A perk with no top level shows its level alone (D250); a one-off shows no
 * level and reads "Owned" once bought (D295).
 */
function ShopRow({
  testId,
  text,
  total,
  level,
  maxLevel,
  cost,
  needs,
  unit,
  intent,
  lockedText,
}: {
  testId: string;
  text: string;
  total: string | null;
  level: number;
  maxLevel: number | null;
  cost: number | null;
  needs?: number | undefined;
  unit: string;
  intent: IdleIntent;
  /** D362, D367: why it cannot be bought yet ("Opens after 1 more festival"); absent once it can. */
  lockedText?: string | undefined;
}) {
  const locked = !!lockedText;
  const top = cost === null && !locked;
  const reason = !top && !locked && needs !== undefined ? `Needs ${num(needs)}` : undefined;
  const oneOff = maxLevel === 1;
  return (
    <div
      data-testid={testId}
      style={{
        ...CARD,
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'start',
        gap: 10,
      }}
    >
      <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>{text}</span>
        {total && (
          <span style={NOTE} data-testid={`${testId}-total`}>
            {total}
          </span>
        )}
        {!oneOff && !locked && (
          <span style={NOTE}>
            <Figs text={maxLevel !== null ? `Level ${level} of ${maxLevel}` : `Level ${level}`} />
          </span>
        )}
      </div>
      {locked ? (
        <span style={{ font: '600 var(--fs-caption)/1.3 var(--font-ui)', color: 'var(--ink)' }}>
          {lockedText}
        </span>
      ) : top ? (
        <span style={{ font: '600 var(--fs-caption)/1.3 var(--font-ui)', color: 'var(--green)' }}>
          {oneOff ? 'Owned' : 'Top level'}
        </span>
      ) : (
        // D441, D442: the price is the amount alone, the currency named in the heading; the
        // accessible name says it in full. 96px wide, so neither line wraps.
        <BuyButton
          width={96}
          secondary
          needs={reason}
          label={`Buy, ${num(cost ?? 0)} ${unit}`}
          onClick={(e) => {
            const el = e.currentTarget as Element;
            const r = useIdle.getState().act(intent);
            if (!r.ok) {
              playSound('error');
              popNumber(el, refusalText(idleContent, r));
              return;
            }
            playSound('buy');
            popNumber(el, oneOff ? 'Owned' : `Level ${level + 1}`);
          }}
        >
          <span style={{ display: 'grid', gap: 3, justifyItems: 'center' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Buy</span>
            <span
              class="num"
              style={{ font: '500 var(--fs-note)/1 var(--font-num)', whiteSpace: 'nowrap' }}
            >
              {num(cost ?? 0)}
            </span>
          </span>
        </BuyButton>
      )}
    </div>
  );
}

/** D454: a perk level bought from the tree's sheet. True when it went through. */
function buyPerk(id: string, el: Element): boolean {
  const level = useIdle.getState().world?.permanent.perks[id] ?? 0;
  const r = useIdle.getState().act({ type: 'buyPerk', perk: id } as IdleIntent);
  if (!r.ok) {
    playSound('error');
    popNumber(el, refusalText(idleContent, r));
    return false;
  }
  playSound('buy');
  popNumber(el, level === 0 ? 'Bought' : `Level ${level + 1}`);
  return true;
}

const traitRow = (t: TraitRow) => (
  <ShopRow
    key={t.id}
    testId={`trait-${t.id}`}
    text={t.text}
    total={t.total}
    level={t.level}
    maxLevel={t.maxLevel}
    cost={t.cost}
    needs={t.needs}
    unit={term('lineage')}
    intent={{ type: 'buyTrait', trait: t.id }}
  />
);

/** D460: the marked Heritage card's line: `Enough for 3 perks, marked.` */
function heritageMarkedLine(n: number): string {
  return n === 1 ? 'Enough for 1 perk, marked.' : `Enough for ${n} perks, marked.`;
}

export function FestivalTab() {
  const world = useIdle((s) => s.world);
  const [confirm, setConfirm] = useState<'festival' | 'pass' | null>(null);
  const [markNote, setMarkNote] = useState('');
  // D460: a badged tab opens on the segment holding the first thing waiting.
  const badge = useBadges()?.festival;
  const arrived = useArrival();
  const [page, setPage] = useState<'year' | 'good'>(() =>
    arrived && badge && firstSegment(badge) === 'good' ? 'good' : 'year',
  );
  const root = useRef<HTMLDivElement>(null);
  useArrive(root, arrived && (badge?.count ?? 0) > 0);
  // D460: opening Perks sees the Heritage earned; its marks stay for the rest of the visit.
  const unseen = (badge?.segments.good ?? 0) > 0;
  const [heritageMarked, setHeritageMarked] = useState(false);
  const [lineageOpen, setLineageOpen] = useState(false);
  useEffect(() => {
    if (page !== 'good') return;
    if (unseen) setHeritageMarked(true);
    useIdle.getState().seeHeritage();
  }, [page, unseen]);
  useScientific();
  if (!world) return null;
  const goodMarks = heritageMarked || unseen ? heritageMarks(world, idleContent) : null;
  const festivalWaiting = (badge?.cards['festival-card'] ?? 0) > 0;
  const preview = idleFestivalPreview(world, idleContent);
  const view = festivalView(idleContent, world, preview);
  // D357: Heritage led by the gain, then before and after at a precision that tells them apart.
  const heritageBefore = world.permanent.heritageEarned;
  const heritageAfter = heritageBefore + view.heritage;
  const growth = heritageGrowthText(heritageBefore, heritageAfter);
  const held = world.permanent.festivals.length;
  // Perks reset when the world is passed on, so year one of every generation folds them.
  const heldNow = idleFestivalsThisGeneration(world);
  const pass = idlePassOnPreview(world, idleContent);
  const lineage = world.permanent.lineage;
  // D356: cheapest first, so "the first you could buy" and the short list both read right.
  const traits = [...traitRows(idleContent, world, idleTraitCost)].sort(
    (a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity),
  );
  const shortTraits = traits.slice(0, 3);
  const moreTraits = traits.slice(3);
  const passText = passOnText(pass, heritageTraitNext(idleContent, world));
  const advice = festivalAdvice(world, idleContent, world.advancedTo);
  // D371: what the player gets and what happens, once, for the card and its sheet.
  const passOnLead = (
    <>
      You get{' '}
      <span class="tnum" data-testid="pass-on-lineage">
        {num(pass.lineage)}
      </span>{' '}
      {term('lineage')}. The {term('world')} starts again. Traits make later {term('generation')}s
      faster.
    </>
  );

  const hold = (el: Element | null) => {
    setConfirm(null);
    const r = useIdle.getState().act(HOLD_FESTIVAL);
    if (!r.ok) {
      playSound('error');
      if (el) popNumber(el, refusalText(idleContent, r));
      return;
    }
    // D276: the festival's own screen follows, where Heritage is spent before the year starts.
    playSound('festival');
    if (el) flyCoins(el, { count: 10 });
  };

  const passOn = (el: Element | null) => {
    setConfirm(null);
    const r = useIdle.getState().act(PASS_ON);
    if (!r.ok) {
      playSound('error');
      if (el) popNumber(el, refusalText(idleContent, r));
      return;
    }
    useIdle.getState().showPassedOn({
      lineage: pass.lineage,
      fromHeritage: pass.fromHeritage,
      fromPlay: pass.fromPlay,
      generation: r.state.permanent.lineage?.generation ?? pass.generation + 1,
    });
  };

  // D442: the pay-off card's figures. The year's earnings against what the festival asks for.
  const yearEarned = world.year.earned;
  const needsCoins = preview.needs ?? 0;
  const ready = view.available;
  const barProgress = ready ? (advice.advice === 'grow' ? advice.progress : 1) : view.progress;
  const progressLine = ready
    ? `${coins(yearEarned)} this year`
    : `${coins(yearEarned)} of ${coins(yearEarned + needsCoins)} this year`;
  const markCap = idleContent.lineage.minFestivals;
  const festivalWord = (n: number) => (n === 1 ? term('festival') : term('festivals'));

  const lineageSection =
    lineage && (lineage.earned > 0 || lineage.generation > 1) ? (
      <>
        {/* D461: one 48px row between the Heritage card and the branches, opening its own sheet. */}
        <button
          type="button"
          data-testid="lineage-row"
          onClick={() => setLineageOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 48,
            padding: '0 var(--edge)',
            textAlign: 'left',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            color: 'var(--ink)',
            cursor: 'pointer',
            font: '600 var(--fs-body)/1.3 var(--font-ui)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>{cap(term('lineage'))} to spend</span>
          <span
            class="num"
            data-testid="lineage-row-points"
            style={{ font: '700 var(--fs-body)/1 var(--font-num)' }}
          >
            {num(lineage.points)}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--ink-2)"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
        {lineageOpen && (
          <Sheet
            title={`${cap(term('lineage'))} to spend`}
            onClose={() => setLineageOpen(false)}
            testId="lineage-sheet"
            zIndex={40}
          >
            <section style={{ display: 'grid', gap: 6 }} data-testid="traits">
              <SectionHead
                title={<Figs text={`${num(lineage.points)} ${term('lineage')} to spend`} />}
              />
              <span style={{ ...NOTE, margin: '0 2px' }}>
                Traits last for every {term('generation')}.
              </span>
              {traits.map(traitRow)}
            </section>
          </Sheet>
        )}
      </>
    ) : null;

  const passOnCard = pass.available ? (
    // D257: after the tenth festival, the world can be passed on.
    <section style={CARD} data-testid="pass-on">
      {/* D371: what you get and what happens, then the button, then the detail. */}
      <p style={LEAD} data-testid="pass-on-lead">
        {passOnLead}
      </p>
      <Button variant="primary" full onClick={() => setConfirm('pass')}>
        {term('passOn')}
      </Button>
      {/* The festival card holds the one "How it works"; the detail here is plain lines. */}
      <div style={{ display: 'grid', gap: 8 }} data-testid="pass-on-how">
        <p style={P} data-testid="pass-on-lose">
          {passText.lose}
        </p>
        <p style={P} data-testid="pass-on-keep">
          {passText.keep}
        </p>
        <p style={P} data-testid="pass-on-pays">
          {passText.pays}
        </p>
        {passText.buys && (
          <p style={P} data-testid="pass-on-buys">
            {passText.buys}
          </p>
        )}
        <span style={SMALL} data-testid="lineage-meter">
          {passText.meter}
        </span>
      </div>
    </section>
  ) : heldNow > 0 ? (
    // D442: quiet and dashed, with a lock, until the world can be passed on (D325).
    <section
      style={{ ...CARD, background: 'transparent', border: '1px dashed var(--ink-2)', gap: 4 }}
      data-testid="lineage-meter-card"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
        <span style={{ color: 'var(--ink-2)', display: 'grid' }}>
          <LockGlyph />
        </span>
        <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>{term('passOn')}</span>
          <span style={NOTE} data-testid="pass-on-locked">
            {pass.festivalsNeeded > 0 ? (
              <>
                <Figs
                  text={`Opens after ${markCap} ${festivalWord(markCap)} · ${heldNow} of ${markCap}`}
                />
              </>
            ) : (
              <>Nothing to pay yet.</>
            )}
          </span>
        </div>
      </div>
      <HowItWorks testId="lineage-how">
        <span style={SMALL} data-testid="lineage-meter">
          {passText.meter}
        </span>
        <span style={SMALL}>{passText.use}</span>
      </HowItWorks>
    </section>
  ) : null;

  return (
    <div ref={root} data-screen-label={term('tabFestival')} style={TAB_BODY}>
      <BookSwitch
        value={page}
        onChange={setPage}
        options={[
          {
            value: 'year',
            label: 'Year',
            badge: badge?.segments.year,
            testId: 'festival-switch-year',
          },
          {
            value: 'good',
            label: 'Perks',
            badge: badge?.segments.good,
            testId: 'festival-switch-good',
          },
        ]}
      />
      {page === 'year' && (
        <>
          <section
            style={{ ...CARD, gap: 6 }}
            class={festivalWaiting ? 'idle-waiting' : undefined}
            data-testid="festival-card"
          >
            {festivalWaiting && <span class="idle-waiting-dot" aria-hidden="true" />}
            <span
              style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}
            >
              The {term('festival')} pays
            </span>
            {/* D357: the gain leads, in the money colour; D431: it is the card's hero figure. */}
            <span
              class="num"
              data-testid="festival-heritage"
              style={{
                font: '700 var(--fs-display)/1.1 var(--font-num)',
                color: 'var(--coin-ink)',
              }}
            >
              {growth.gain}
              {/* D459: the unit always shows, in the UI font beside the mono number. */}
              <span
                data-testid="festival-heritage-unit"
                style={{ font: '600 var(--fs-title)/1.2 var(--font-ui)', marginLeft: 6 }}
              >
                {cap(term('heritage'))}
              </span>
            </span>
            {ready && heritageBefore > 0 && (
              <span style={NOTE} data-testid="festival-heritage-range">
                {growth.detail}
              </span>
            )}
            <div
              class="juice-bar"
              role="progressbar"
              aria-label={
                ready
                  ? `How long until the ${term('festival')} is ready`
                  : `This year's earnings towards the ${term('festival')}`
              }
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.floor(barProgress * 100)}
              data-testid="festival-ready-bar"
              style={{ height: '6px', borderRadius: '6px', width: '100%', marginTop: 2 }}
            >
              <span style={{ background: 'var(--coin)', transform: `scaleX(${barProgress})` }} />
            </div>
            <span
              style={{ font: '500 var(--fs-caption)/1.3 var(--font-ui)' }}
              data-testid="festival-progress"
            >
              <Figs text={progressLine} />
            </span>
            {/* D371: one plain line: good now, or how long to wait. */}
            {ready && (
              <p style={{ ...NOTE, margin: 0 }} data-testid="festival-advice">
                {advice.long ?? `Ready to ${term('hold')}.`}
              </p>
            )}
            {ready ? (
              <Button variant="primary" full onClick={() => setConfirm('festival')}>
                <span style={{ display: 'grid', gap: 3, justifyItems: 'center' }}>
                  <span
                    style={{ font: '600 var(--fs-label)/1 var(--font-ui)', whiteSpace: 'nowrap' }}
                  >
                    {cap(term('hold'))} the {term('festival')} now
                  </span>
                  <span
                    class="num"
                    style={{ font: '500 var(--fs-note)/1 var(--font-num)', whiteSpace: 'nowrap' }}
                  >
                    +{num(view.heritage)} {term('heritage')}
                  </span>
                </span>
              </Button>
            ) : (
              <>
                <Button variant="primary" full disabled explain={view.goal}>
                  <span style={{ display: 'grid', gap: 3, justifyItems: 'center', minWidth: 0 }}>
                    <span
                      style={{ font: '600 var(--fs-label)/1 var(--font-ui)', whiteSpace: 'nowrap' }}
                    >
                      {cap(term('hold'))} the {term('festival')} now
                    </span>
                    {/* D459: the shortfall is the button's second line, never struck. */}
                    <span
                      class="num off-needs"
                      data-testid="festival-needs"
                      style={{ font: '500 var(--fs-note)/1 var(--font-num)', whiteSpace: 'nowrap' }}
                    >
                      <span style={UNSTRUCK}>{`Needs ${coins(needsCoins)} more`}</span>
                    </span>
                  </span>
                </Button>
              </>
            )}
            <HowItWorks testId="festival-how">
              <p style={P}>
                The {term('festival')} ends the year and pays {term('heritage')}.{' '}
                {cap(term('heritage'))} raises all your income for good
                {heldNow === 0 ? '.' : ', and buys perks below.'}
              </p>
              {ready && (
                <>
                  {view.fromGoals && (
                    <span style={SMALL} data-testid="festival-from-goals">
                      {view.fromGoals}
                    </span>
                  )}
                  <span style={SMALL}>{markGainLine(view.mark, term('world'))}</span>
                </>
              )}
              <span style={SMALL}>
                {RESETS} {STAYS}
              </span>
            </HowItWorks>
          </section>

          {heldNow > 0 && (
            <>
              {/* D442, D459: two figures on one card, then the marks as a named grid, then pass on. */}
              <div
                style={{
                  ...CARD,
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  alignItems: 'start',
                }}
                data-testid="festival-heritage-held"
              >
                <FigureCell label={`${cap(term('heritage'))} to spend`}>
                  <span
                    class="num"
                    data-testid="heritage-held"
                    style={{
                      font: '700 var(--fs-title)/1.2 var(--font-num)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {num(world.permanent.heritage)}
                  </span>
                </FigureCell>
                <FigureCell label="Income bonus">
                  <span
                    class="num"
                    style={{
                      font: '700 var(--fs-title)/1.2 var(--font-num)',
                      color: preview.bonusNow > 1 ? 'var(--green)' : 'var(--ink)',
                    }}
                  >
                    {bonusText(Math.max(1, preview.bonusNow))}
                  </span>
                </FigureCell>
              </div>
              <section style={{ ...CARD, gap: 8 }} data-testid="festival-marks-card">
                <span style={KICKER}>
                  <Figs text={`Marks held · ${held}`} />
                </span>
                <div
                  role="list"
                  aria-label={`${held} ${festivalWord(held)} held`}
                  data-testid="festival-marks"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 8,
                  }}
                >
                  {world.permanent.festivals.slice(-markCap).map((f, i, shown) => {
                    const isNew = i === shown.length - 1;
                    return (
                      <MarkTile key={i} mark={f.mark} isNew={isNew} listItem onTap={setMarkNote} />
                    );
                  })}
                </div>
                {markNote && (
                  <span style={NOTE} data-testid="festival-mark-note" role="status">
                    {markNote}
                  </span>
                )}
                {held > markCap && (
                  <span style={NOTE}>
                    <Figs text={`and ${held - markCap} more`} />
                  </span>
                )}
              </section>
            </>
          )}
          {passOnCard}
        </>
      )}
      {page === 'good' && (
        <>
          {heldNow === 0 ? (
            // D261: before the generation's first festival there is nothing to spend, so the
            // perks are one line.
            <section style={CARD} data-testid="festival-perks">
              <span style={P}>Perks open after your first {term('festival')}.</span>
            </section>
          ) : (
            <>
              {/* D454: the Heritage on offer, then the permanent tree. */}
              <section
                style={{ ...CARD, gap: 4 }}
                class={goodMarks ? 'idle-waiting' : undefined}
                data-testid="heritage-card"
              >
                {goodMarks && <span class="idle-waiting-dot" aria-hidden="true" />}
                <span
                  style={{
                    font: '600 var(--fs-caption)/1.2 var(--font-ui)',
                    color: 'var(--ink-2)',
                  }}
                >
                  {cap(term('heritage'))} to spend
                </span>
                <span
                  class="num"
                  data-testid="heritage-held"
                  style={{
                    font: '700 var(--fs-heading)/1.2 var(--font-num)',
                    color: 'var(--coin-ink)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {num(world.permanent.heritage)}
                </span>
                {goodMarks && goodMarks.items.length > 0 ? (
                  <span style={NOTE} data-testid="heritage-marked">
                    <Figs text={heritageMarkedLine(goodMarks.items.length)} />
                  </span>
                ) : (
                  <span style={NOTE}>
                    Kept when you {lower(term('passOn'))}. Spend it anywhere: every open step can
                    level up.
                  </span>
                )}
              </section>
              {lineageSection}
              <section data-testid="festival-perks">
                <TreePanel
                  {...perkTree(world, idleContent, heritageMarked || unseen)}
                  testId="perk-tree"
                  buy={buyPerk}
                />
              </section>
            </>
          )}
          {heldNow === 0 && lineageSection}
        </>
      )}
      {confirm === 'festival' && (
        <Sheet
          title={`${cap(term('hold'))} the ${term('festival')}`}
          onClose={() => setConfirm(null)}
          testId="festival-confirm"
          zIndex={40}
        >
          <p style={P}>
            <Figs text={`You earn ${num(view.heritage)} ${term('heritage')}: ${view.bonus}.`} />
          </p>
          <p style={P}>{RESETS}</p>
          <p style={P}>{STAYS}</p>
          <Button variant="primary" full onClick={(e) => hold(e.currentTarget as Element)}>
            {cap(term('hold'))} the {term('festival')}
          </Button>
          <Button full onClick={() => setConfirm(null)}>
            Cancel
          </Button>
        </Sheet>
      )}
      {confirm === 'pass' && (
        <Sheet
          title={term('passOn')}
          onClose={() => setConfirm(null)}
          testId="pass-on-confirm"
          zIndex={40}
        >
          <p style={LEAD} data-testid="pass-on-offer">
            {passOnLead}
          </p>
          <div style={{ display: 'grid', gap: 4 }}>
            <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>You lose</span>
            <ul style={LIST}>
              {passText.loseList.map((x) => (
                <li key={x} style={P}>
                  {cap(x)}
                </li>
              ))}
            </ul>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>You keep</span>
            <ul style={LIST}>
              {passText.keepList.map((x) => (
                <li key={x} style={P}>
                  {cap(x)}
                </li>
              ))}
            </ul>
          </div>
          {traits.length > 0 && (
            <div style={{ display: 'grid', gap: 4 }}>
              <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>Traits</span>
              <ul data-testid="pass-on-confirm-traits" style={LIST}>
                {shortTraits.map((t) => (
                  <li key={t.id} style={SMALL}>
                    <Figs text={`${t.text}: ${num(t.cost ?? 0)} ${term('lineage')}`} />
                  </li>
                ))}
              </ul>
              {moreTraits.length > 0 && (
                <details data-testid="pass-on-all-traits">
                  <summary style={SUMMARY}>
                    <Figs text={`and ${moreTraits.length} more`} />
                  </summary>
                  <ul style={LIST}>
                    {moreTraits.map((t) => (
                      <li key={t.id} style={SMALL}>
                        <Figs text={`${t.text}: ${num(t.cost ?? 0)} ${term('lineage')}`} />
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <Button variant="primary" full onClick={(e) => passOn(e.currentTarget as Element)}>
            {term('passOn')}
          </Button>
          <Button full onClick={() => setConfirm(null)}>
            Cancel
          </Button>
        </Sheet>
      )}
    </div>
  );
}

/** D431: a held mark, 58px, in the money colour; this year's to come, the inset ground. */
const MARK_TILE: JSX.CSSProperties = {
  flex: 'none',
  width: 58,
  height: 58,
  boxSizing: 'border-box',
  padding: 2,
  borderRadius: 'var(--radius-md)',
  display: 'grid',
  placeItems: 'center',
  font: '600 var(--fs-badge)/1.2 var(--font-ui)',
  textAlign: 'center',
  overflowWrap: 'anywhere',
};
const MARK_TILE_HELD: JSX.CSSProperties = {
  ...MARK_TILE,
  background: 'var(--coin)',
  color: 'var(--on-coin)',
};

const KICKER: JSX.CSSProperties = {
  font: '600 var(--fs-caption)/1.2 var(--font-ui)',
  color: 'var(--ink-2)',
};

/** D442: a mark on the year-end card: 70px tall, 8px radius, icon over a name of two lines at most. */
const MARK_GRID_TILE: JSX.CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  height: 70,
  minWidth: 0,
  padding: '6px 6px',
  borderRadius: 8,
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  justifyItems: 'center',
  alignItems: 'center',
  gap: 4,
  background: 'var(--surface-2)',
  color: 'var(--ink)',
  overflow: 'hidden',
};
const MARK_GRID_NEW: JSX.CSSProperties = {
  ...MARK_GRID_TILE,
  background: 'var(--surface)',
  border: '2px solid var(--coin)',
  padding: '4px 4px',
};
const MARK_GRID_NAME: JSX.CSSProperties = {
  font: '500 var(--fs-note)/1.15 var(--font-ui)',
  textAlign: 'center',
  color: 'var(--ink)',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflowWrap: 'anywhere',
  maxWidth: '100%',
};
const MARK_NEW_TAG: JSX.CSSProperties = {
  position: 'absolute',
  top: 3,
  right: 3,
  padding: '1px 4px',
  borderRadius: 4,
  background: 'var(--coin)',
  color: 'var(--on-coin)',
  font: '700 var(--fs-badge)/1.2 var(--font-ui)',
};

/** One of the three figures: an 11px label over its value. */
/** D470: a mark's tile; tapped, it says in a line that the mark stays on the map. */
function MarkTile({
  mark,
  isNew,
  pop,
  listItem,
  onTap,
}: {
  mark: string;
  isNew: boolean;
  pop?: boolean;
  listItem?: boolean;
  onTap: (note: string) => void;
}) {
  return (
    <button
      type="button"
      role={listItem ? 'listitem' : undefined}
      data-testid={`festival-mark-${mark}`}
      class={pop ? 'mo-pop' : undefined}
      onClick={() => onTap(markStaysLine(markName(mark)))}
      style={{
        ...(isNew ? MARK_GRID_NEW : MARK_GRID_TILE),
        minHeight: 44,
        border: isNew ? '2px solid var(--coin)' : 'none',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {isNew && <span style={MARK_NEW_TAG}>New</span>}
      <Icon name={markIcon(mark)} size={22} />
      <span style={MARK_GRID_NAME}>{cap(markName(mark))}</span>
    </button>
  );
}

function FigureCell({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0, alignContent: 'start' }}>
      <span style={{ font: '600 var(--fs-note)/1.25 var(--font-ui)', color: 'var(--ink-2)' }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/** D431: a gold ring spreading from the centre, for the biggest moment. Still, none. */
function GoldRing() {
  const reduced = useReducedMotion();
  if (reduced) return null;
  return (
    <span class="mo-ring mo-ring-gold" aria-hidden="true" style={{ left: '50%', top: '38%' }} />
  );
}

/** An intent with no arguments; see `UPGRADE_BARN` in IdleApp for why it is cast. */
const START_YEAR = { type: 'startYear' } as IdleIntent;

/**
 * D276: the festival just held. The Heritage it paid, the mark it left, the perk shop, and
 * "Start year N": perks bought here act on the year about to begin, which does not start
 * until the player taps it (or after a while away, D276).
 */
export function FestivalPending() {
  const world = useIdle((s) => s.world);
  // D439: the moment shows once as a modal card; Close leaves the shop and the start button.
  const [momentOpen, setMomentOpen] = useState(true);
  const [markNote, setMarkNote] = useState('');
  useScientific();
  if (!world?.festivalPending) return null;
  const pending = world.festivalPending;
  const record = world.permanent.festivals[world.permanent.festivals.length - 1];
  // D389: the festival is already in the record, so this is the next year as the header counts it.
  const next = yearOfGeneration(world);
  // D388: a world passed on waits here too, to choose traits before its new generation starts.
  const passed = pending.passedOn === true;
  const earnedPass = useIdle.getState().passedOn;
  const generation = world.permanent.lineage?.generation ?? 2;
  const points = world.permanent.lineage?.points ?? 0;
  const traits = [...traitRows(idleContent, world, idleTraitCost)].sort(
    (a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity),
  );
  const traitsKept = Object.values(world.permanent.lineage?.traits ?? {}).filter(
    (level) => level > 0,
  ).length;
  const marksKept = world.permanent.festivals.length;
  // D473: the same tree and marks as Festival → Perks, always marked here (no seen gate).
  const pendingTree = perkTree(world, idleContent, true);
  const pendingMarks = pendingTree.marks;
  const start = (el: Element) => {
    const r = useIdle.getState().act(START_YEAR);
    if (!r.ok) {
      playSound('error');
      popNumber(el, refusalText(idleContent, r));
      return;
    }
    playSound('welcome');
    if (passed) useIdle.getState().closePassedOn();
    else useIdle.getState().closeFestival();
  };
  if (passed)
    return (
      <div
        data-screen-label={term('tabFestival')}
        data-testid="pass-on-pending"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gap: 12,
          padding: 12,
          alignContent: 'start',
        }}
      >
        <GoldRing />
        {momentOpen && (
          <Fanfare
            title={`A new ${term('generation')}`}
            amount={`${earnedPass ? '+' : ''}${num(earnedPass ? earnedPass.lineage : points)} ${term('lineage')}`}
            amountTestId="pass-on-earned"
            money={false}
            facts={[
              { label: 'Traits kept', value: String(traitsKept) },
              { label: `${cap(term('festival'))} marks kept`, value: String(marksKept) },
              { label: cap(term('generation')), value: String(generation) },
            ]}
            onClose={() => setMomentOpen(false)}
          />
        )}
        <section style={CARD} class="mo-rise-card">
          <span style={KICKER}>A new {term('generation')}</span>
          {/* D431: the generations so far, and the new one popping in last. */}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {Array.from({ length: generation }, (_, i) => (
              <span
                key={i}
                class={i === generation - 1 ? 'mo-pop' : undefined}
                style={
                  i === generation - 1
                    ? { ...MARK_TILE_HELD, outline: '2px solid var(--accent)', outlineOffset: 1 }
                    : MARK_TILE_HELD
                }
              >
                {cap(term('generation'))} {i + 1}
              </span>
            ))}
          </div>
          <p style={P}>
            Your {term('world')} carries on as a new {term('generation')}. Every {term('festival')}{' '}
            mark stays.
          </p>
          <p style={P}>
            Choose traits now. They change the rules for every {term('generation')}, and start with
            this one.
          </p>
          <Button variant="primary" full onClick={(e) => start(e.currentTarget as Element)}>
            Start {term('generation')} {generation}
          </Button>
        </section>
        <section style={{ ...CARD, gap: 0 }} data-testid="traits">
          <h3 style={{ ...H3, marginBottom: 6 }}>
            <Figs text={`${num(points)} ${term('lineage')} to spend`} />
          </h3>
          {traits.map(traitRow)}
        </section>
      </div>
    );
  return (
    <div
      data-screen-label={term('tabFestival')}
      data-testid="festival-pending"
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'grid',
        gap: 12,
        padding: 12,
        alignContent: 'start',
      }}
    >
      {momentOpen && (
        <Fanfare
          title={`${cap(term('harvest'))} ${term('festival')}`}
          amount={`+${num(pending.heritage)} ${term('heritage')}`}
          amountTestId="festival-earned"
          facts={[
            { label: cap(term('heritage')), value: num(world.permanent.heritage) },
            {
              label: 'Income bonus',
              value: bonusText(idleHeritageBonus(idleContent, world.permanent.heritageEarned)),
            },
            ...(record ? [{ label: 'New mark', value: cap(markName(record.mark)) }] : []),
          ]}
          onClose={() => setMomentOpen(false)}
        />
      )}
      <section style={CARD} class="mo-rise-card">
        <span style={KICKER}>
          {cap(term('harvest'))} {term('festival')}
        </span>
        {record && <p style={P}>{markGainLine(markName(record.mark), term('world'))}</p>}
        {/* D442: the marks the world holds, three a row; this year's rings in the money colour. */}
        <div
          data-testid="festival-marks-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}
        >
          {world.permanent.festivals.map((f, i, all) => {
            const isNew = i === all.length - 1;
            return <MarkTile key={i} mark={f.mark} isNew={isNew} pop={isNew} onTap={setMarkNote} />;
          })}
        </div>
        {markNote && (
          <p style={P} data-testid="festival-mark-note" role="status">
            {markNote}
          </p>
        )}
        <p style={P}>
          Spend {term('heritage')} on perks before you start year {next}.
        </p>
        <Button variant="primary" full onClick={(e) => start(e.currentTarget as Element)}>
          Start year {next}
        </Button>
      </section>
      <section
        style={{ ...CARD, gap: 4 }}
        class={pendingMarks && pendingMarks.items.length > 0 ? 'idle-waiting' : undefined}
        data-testid="heritage-card"
      >
        {pendingMarks && pendingMarks.items.length > 0 && (
          <span class="idle-waiting-dot" aria-hidden="true" />
        )}
        <span style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
          {cap(term('heritage'))} to spend
        </span>
        <span
          class="num"
          data-testid="heritage-held"
          style={{
            font: '700 var(--fs-heading)/1.2 var(--font-num)',
            color: 'var(--coin-ink)',
            overflowWrap: 'anywhere',
          }}
        >
          {num(world.permanent.heritage)}
        </span>
        {pendingMarks && pendingMarks.items.length > 0 && (
          <span style={NOTE} data-testid="heritage-marked">
            <Figs text={heritageMarkedLine(pendingMarks.items.length)} />
          </span>
        )}
      </section>
      <section data-testid="festival-perks">
        <TreePanel {...pendingTree} testId="perk-tree" buy={buyPerk} />
      </section>
    </div>
  );
}
